import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Checks to see if the browser is on a mobile device.
 *
 * Using feature detection instead of userAgent to determine if the device supports touch events.
 * https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/Browser_detection_using_the_user_agent#alternatives_to_ua_sniffing
 *
 * @return {boolean} whether the device supports touch events
 */
function hasTouchSupport() {
  return 'ontouchstart' in window || window.navigator.maxTouchPoints > 0;
}

/**
 * Adds share interactivity to the store.
 */
const { state, actions } = store('bigbite-share-button', {
  state: {
    get data() {
      const { ref } = getElement();
      const parent = ref.closest('.wp-block-bigbite-share-button');
      return {
        url: parent.dataset.shareUrl,
        text: parent.dataset.shareText,
        title: parent.dataset.shareTitle,
      };
    },
    get supportsNativeShare() {
      const context = getContext();
      return hasTouchSupport() && !!navigator.share && navigator.canShare(context.data);
    },
    get modalCopyText() {
      const context = getContext();
      return context.hasCopied ? state.copied_text : state.popover_copy_text;
    },
    get copyText() {
      const context = getContext();
      return !context.hasPopover && context.hasCopied ? state.copied_text : state.default_text;
    },
  },
  actions: {
    *share() {
      const { data } = state;

      if (!data.url) {
        return;
      }
      // Decode URL to make Arabic characters human-readable
      try {
        data.url = decodeURI(data.url);
      } catch (error) {
        // If decodeURI fails, use the original URL
        // eslint-disable-next-line no-console
        console.warn('Could not decode URL:', data.url, error);
      }

      if (state.supportsNativeShare) {
        try {
          yield navigator.share(data);
        } catch (error) {
          if (error.name !== 'AbortError') {
            // eslint-disable-next-line no-console
            console.error('Error sharing:', error);
          }
        }
        return;
      }

      const { hasPopover } = getContext();
      if (!hasPopover) {
        yield actions.copy();
      }
    },
    *copy() {
      let { url } = state.data;

      if (!url) {
        return;
      }

      // Decode URL to make Arabic characters human-readable
      try {
        url = decodeURI(url);
      } catch (error) {
        // If decodeURI fails, use the original URL
        // eslint-disable-next-line no-console
        console.warn('Could not decode URL:', url, error);
      }

      try {
        yield navigator.clipboard.writeText(url);

        const context = getContext();
        context.hasCopied = true;

        setTimeout(() => {
          context.hasCopied = false;
        }, 1500);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error copying url:', error);
      }
    },
  },
});
